import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { createBooklet, getBookletSources } from "../../services/bookletService";
import { getQuestionFolders } from "../../services/questionService";
import { getApiErrorCode } from "../../utils/apiError";
import { Button } from "../../components/forms/Buttons/Button";
import Select from "../../components/forms/Select/Select";
import { HelpTitle } from "../../components/Help/HelpButton";
import BtsGuide from "../../components/Help/guides/BtsGuide";
import { cardClass, focusRing, linkButtonClass, Spinner } from "../Exams/TestBuilder/ui";
import {
  backLinkClass,
  bookletErrorMessage,
  Codes,
  bookletFolders,
  btsRound,
  describedBy,
  fieldClass,
  FieldError,
  fieldErrors,
  focusField,
  GRADES,
  invalidClass,
  labelClass,
  LanguageChoice,
  LANGUAGES,
  linkTapClass,
  listHref,
  useTakenCodes,
  variantCode,
  variantHref,
} from "./bts";
import VariantDigits from "./VariantDigits";

const FIELDS = ["folder", "grade", "language", "name", "description", "round_number", "variant_numbers", "duration_minutes"];
// Where each field is, in the form's order: a failed check focuses the first one with an error (a select's input,
// the first digit).
const FOCUS_ID = {
  folder: "bts-create-folder",
  grade: "bts-create-grade",
  language: "bts-create-language-field",
  name: "bts-create-name",
  round_number: "bts-create-round_number",
  duration_minutes: "bts-create-duration_minutes",
  variant_numbers: "bts-create-digits-group",
  description: "bts-create-description",
};
const errorId = (field) => `${FOCUS_ID[field]}-error`;

// "БТС-9 · 10-сынып · ҚАЗ" (with "· 1-күн" when the day is given), written in the booklet's own language.
export const suggestedName = (t, folderName, grade, language, day) =>
  folderName && grade && language
    ? t(day ? "bts.create.suggested_name_day" : "bts.create.suggested_name", {
        lng: language,
        folder: folderName,
        grade,
        day,
        stream: t(`bts.languages.${language}`, { lng: language }),
      })
    : "";

// A grade's booklets go by stream and day, two versions each: ҚАЗ day 1 takes digits 1–2, day 2 3–4; РУС day 1
// 5–6, day 2 7–8 (so 7th grade ҚАЗ day 1 prints 2071 and 2072 in БТС-2).
export const DAYS = [1, 2];
export const dayDigits = (language, day) => {
  const first = (language === "ru" ? 4 : 0) + (day - 1) * 2 + 1;
  return [first, first + 1];
};

// The booklet's day, as the stream's choice next to it.
function DayChoice({ id, value, onChange }) {
  const [t] = useTranslation();
  return (
    <fieldset>
      <legend className={labelClass}>{t("bts.fields.day")}</legend>
      <div className="inline-flex w-full rounded-lg bg-white p-0.5 ring-1 ring-inset ring-slate-300 sm:w-auto">
        {DAYS.map((day) => (
          <label
            key={day}
            className={`relative flex h-9 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md px-4 text-sm font-medium transition duration-200 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-blue-600 max-sm:h-11 sm:flex-none ${
              value === day ? "bg-slate-800 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <input type="radio" name={id} value={day} checked={value === day} onChange={() => onChange(day)} className="sr-only" />
            {t("bts.create.day_n", { n: day })}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// The subjects of the folder for this grade and stream with a confirmed variant: what the booklet can take. Their
// variants open in a new tab (the form stays); back on this tab, they are read again.
function SourcesHint({ folder, grade, language }) {
  const [t] = useTranslation();
  const [state, setState] = useState({ key: "", data: null, error: false });
  const [refresh, setRefresh] = useState(0);
  const key = folder && grade && language ? `${folder}:${grade}:${language}` : "";

  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && setRefresh((n) => n + 1);
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    if (!key) return undefined;
    const controller = new AbortController();
    let active = true;
    getBookletSources({ folder, grade, language }, { signal: controller.signal })
      .then((data) => active && setState({ key, data, error: false }))
      // A read again that fails keeps what is shown.
      .catch(() => active && setState((prev) => (prev.key === key && prev.data ? prev : { key, data: null, error: true })));
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, folder, grade, language, refresh]);

  if (!key) return null;
  if (state.key !== key) {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-500" role="status">
        <Spinner className="h-3.5 w-3.5" />
        {t("bts.create.sources_loading")}
      </p>
    );
  }
  if (state.error) return null;
  const subjects = state.data?.subjects || [];
  const ready = subjects.filter((s) => s.variants?.some((v) => v.status === "confirmed"));
  const waiting = subjects.filter((s) => !s.variants?.some((v) => v.status === "confirmed"));
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm" aria-live="polite">
      <p className="flex items-start gap-2 font-medium text-slate-800">
        <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
        {subjects.length === 0
          ? t("bts.create.sources_none")
          : t("bts.create.sources_ready", { count: ready.length, total: subjects.length })}
      </p>
      {subjects.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {[...ready, ...waiting].map((s) => {
            const ok = ready.includes(s);
            return (
              <li key={s.folder.uuid}>
                <Link
                  to={variantHref(s.folder.uuid)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition duration-200 max-sm:px-3.5 max-sm:text-sm ${linkTapClass} ${focusRing} ${
                    ok ? "bg-emerald-50 text-emerald-800 ring-emerald-600/20 hover:bg-emerald-100" : "bg-white text-slate-600 ring-slate-300 hover:bg-slate-100"
                  }`}
                  title={ok ? t("bts.create.subject_ready") : t("bts.create.subject_waiting")}
                >
                  {ok ? (
                    <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400" aria-hidden="true" />
                  )}
                  {s.folder.name}
                  <span className="sr-only">: {ok ? t("bts.create.subject_ready") : t("bts.create.subject_waiting")}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Admin: a new BTS booklet (/bts/new; ?folder=&grade=&language= preset from the list's filters).
export default function BtsCreate() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const back = listHref(location.state);
  const [params] = useSearchParams();

  const [folders, setFolders] = useState([]);
  const [foldersState, setFoldersState] = useState("loading");
  // The name, the BTS number and the digits follow the folder, grade and stream until they are changed by hand.
  const [form, setForm] = useState(() => ({
    folder: params.get("folder") || "",
    grade: GRADES.includes(Number(params.get("grade"))) ? Number(params.get("grade")) : "",
    language: LANGUAGES.includes(params.get("language")) ? params.get("language") : "kk",
    day: 1,
    name: "",
    nameEdited: false,
    round: "",
    roundEdited: false,
    digits: [1],
    digitsEdited: false,
    duration: "",
    description: "",
  }));
  const initialRef = useRef(form);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  // The digits and the BTS number by hand (they follow the stream and the day, and the folder, until then).
  const [showDigits, setShowDigits] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    getQuestionFolders({ signal: controller.signal })
      .then((list) => {
        if (!active) return;
        setFolders(bookletFolders(Array.isArray(list) ? list : list?.results || []));
        setFoldersState("ready");
      })
      .catch(() => active && setFoldersState("error"));
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const folder = folders.find((f) => f.uuid === form.folder) || null;
  const folderRound = folder ? btsRound(folder.name) : null;
  const round = form.roundEdited ? form.round : folderRound ?? "";
  const roundNumber = Number(round) || null;
  // A number other than the folder's prints another BTS's codes.
  const mismatch = !!folderRound && !!roundNumber && roundNumber !== folderRound;
  const suggestion = suggestedName(t, folder?.name, form.grade, form.language, form.day);
  const name = form.nameEdited ? form.name : suggestion;
  const { byDigit, ready } = useTakenCodes({ round: roundNumber, grade: Number(form.grade) || null });
  // Until chosen by hand: the stream's and the day's two digits.
  const schemeDigits = dayDigits(form.language, form.day);
  const digits = form.digitsEdited ? form.digits : schemeDigits;
  const codes = digits.map((digit) => variantCode(roundNumber, Number(form.grade) || null, digit)).filter(Boolean);
  const takenCodes = ready ? digits.filter((digit) => byDigit.has(digit)).map((digit) => variantCode(roundNumber, Number(form.grade), digit)) : [];
  // The hand-made part opens by itself when the folder gives no number or something there is wrong.
  const digitsForced = (!!form.folder && !folderRound) || !!errors.round_number || !!errors.variant_numbers || mismatch;
  const digitsOpen = showDigits || digitsForced;

  const folderOptions = useMemo(() => folders.map((f) => ({ value: f.uuid, label: f.name })), [folders]);
  const gradeOptions = GRADES.map((g) => ({ value: g, label: t("bts.grade_value", { grade: g }) }));

  // What was typed or chosen: leaving the page then asks first.
  const dirty = JSON.stringify(form) !== JSON.stringify(initialRef.current);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const guardLeave = (event) => {
    if (!dirty || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!link || link.target === "_blank") return;
    if (!window.confirm(t("bts.leave_confirm"))) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const set = (changes, field) => {
    // A number typed as the folder's own (or none) follows another folder: it was not a number of its own.
    const follows = "folder" in changes && (!form.roundEdited || form.round === "" || Number(form.round) === folderRound);
    setForm((prev) => ({ ...prev, ...changes, ...(follows ? { round: "", roundEdited: false } : {}) }));
    // The field's error goes, and so do the errors of the name and the number the change fills in.
    const next = { ...form, ...changes };
    const nextFolder = folders.find((f) => f.uuid === next.folder)?.name;
    const fields = [
      field,
      !next.nameEdited && suggestedName(t, nextFolder, next.grade, next.language, next.day) && "name",
      follows && btsRound(nextFolder) && "round_number",
    ].filter(Boolean);
    setErrors((prev) => (fields.some((f) => prev[f]) ? { ...prev, ...Object.fromEntries(fields.map((f) => [f, ""])) } : prev));
  };

  const validate = () => {
    const next = {};
    if (!form.folder) next.folder = t("bts.create.required");
    if (!form.grade) next.grade = t("bts.create.required");
    if (!name.trim()) next.name = t("bts.create.required");
    if (!roundNumber || roundNumber < 1 || roundNumber > 9) next.round_number = t("bts.create.round_invalid");
    if (!digits.length) next.variant_numbers = t("bts.digits.none");
    const duration = form.duration === "" ? null : Number(form.duration);
    if (duration !== null && !(Number.isInteger(duration) && duration >= 1 && duration <= 1440)) next.duration_minutes = t("bts.create.duration_invalid");
    return next;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const found = validate();
    setErrors(found);
    setFormError("");
    if (Object.keys(found).length) {
      // The first field with an error takes the focus.
      focusField(FOCUS_ID[Object.keys(FOCUS_ID).find((field) => found[field])]);
      return;
    }
    setSaving(true);
    try {
      const body = {
        folder: form.folder,
        grade: Number(form.grade),
        language: form.language,
        name: name.trim(),
        round_number: roundNumber,
        variant_numbers: digits,
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        ...(form.duration !== "" ? { duration_minutes: Number(form.duration) } : {}),
      };
      const booklet = await createBooklet(body);
      toast.success(t("bts.create.created", { name: booklet.name }));
      // The booklet keeps the way back to the list as it was left.
      navigate(`/bts/${booklet.uuid}`, { replace: true, state: location.state });
    } catch (err) {
      const code = getApiErrorCode(err);
      const message = bookletErrorMessage(t, err, "bts.create.failed");
      const byCode = { not_exam_folder: "folder", round_required: "round_number", variant_numbers_invalid: "variant_numbers" };
      const fields = fieldErrors(err, FIELDS);
      if (byCode[code]) setErrors({ [byCode[code]]: message });
      else if (Object.keys(fields).length) setErrors(fields);
      else setFormError(message);
      setSaving(false);
    }
  };

  const invalid = (field) => (errors[field] ? invalidClass : "");
  const describe = (field, ...more) => describedBy(...more, errors[field] && errorId(field));

  return (
    // The form's column, centred on the page with its back link and title.
    <div className="mx-auto flex w-full min-w-0 max-w-4xl flex-col gap-5 motion-safe:animate-fade-in" onClickCapture={guardLeave}>
      <Link to={back} className={backLinkClass}>
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        {t("nav.bts")}
      </Link>
      <div className="min-w-0">
        <HelpTitle helpId="bts" helpTitle={t("help.bts.title")} guide={<BtsGuide />}>
          {t("bts.create.title")}
        </HelpTitle>
        <p className="mt-1 text-sm text-slate-500">{t("bts.create.intro")}</p>
      </div>

      <form onSubmit={submit} noValidate className={`${cardClass} w-full p-4 sm:p-6`}>
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]">
            <div id={FOCUS_ID.folder}>
              <Select
                label={t("bts.fields.folder")}
                inputId="bts-create-folder-input"
                ariaInvalid={!!errors.folder}
                ariaErrorMessage={errors.folder ? errorId("folder") : undefined}
                options={folderOptions}
                value={form.folder}
                onChange={(option) => set({ folder: option?.value || "" }, "folder")}
                loading={foldersState === "loading"}
                placeholder={t("bts.create.folder_placeholder")}
              />
              <FieldError id={errorId("folder")}>{errors.folder}</FieldError>
              {foldersState === "ready" && folders.length === 0 && (
                <p className="mt-1.5 text-sm text-amber-800">{t("bts.create.no_folders")}</p>
              )}
              {foldersState === "error" && (
                <p role="alert" className="mt-1.5 text-sm text-red-600">
                  {t("bts.create.folders_error")}
                </p>
              )}
            </div>
            <div id={FOCUS_ID.grade}>
              <Select
                label={t("bts.fields.grade")}
                inputId="bts-create-grade-input"
                ariaInvalid={!!errors.grade}
                ariaErrorMessage={errors.grade ? errorId("grade") : undefined}
                options={gradeOptions}
                value={form.grade}
                onChange={(option) => set({ grade: option?.value || "" }, "grade")}
                placeholder={t("bts.create.grade_placeholder")}
              />
              <FieldError id={errorId("grade")}>{errors.grade}</FieldError>
            </div>
            <div id={FOCUS_ID.language}>
              <LanguageChoice id="bts-create-language" value={form.language} onChange={(language) => set({ language }, "language")} />
              <FieldError id={errorId("language")}>{errors.language}</FieldError>
            </div>
            <DayChoice id="bts-create-day" value={form.day} onChange={(day) => set({ day }, "variant_numbers")} />
          </div>

          <SourcesHint folder={form.folder} grade={form.grade} language={form.language} />

          {/* The codes once the programme and the grade are chosen (or when something there needs a hand). */}
          {((form.folder && form.grade) || digitsOpen) && (
            <section aria-labelledby="bts-create-codes-title" className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                  <h3 id="bts-create-codes-title" className="text-sm font-semibold text-slate-900">
                    {t("bts.create.codes_title")}
                  </h3>
                  <Codes codes={codes} taken={takenCodes} className="gap-2 [&>span]:px-2.5 [&>span]:py-1 [&>span]:text-base" />
                </div>
                <div className="flex flex-wrap items-center gap-x-3">
                  {form.digitsEdited && JSON.stringify(digits) !== JSON.stringify(schemeDigits) && (
                    <button
                      type="button"
                      onClick={() => set({ digits: schemeDigits, digitsEdited: false }, "variant_numbers")}
                      className={`${linkButtonClass} ${linkTapClass}`}
                    >
                      {t("bts.create.digits_scheme")}
                    </button>
                  )}
                  {!digitsForced && (
                    <button
                      type="button"
                      onClick={() => setShowDigits((v) => !v)}
                      aria-expanded={digitsOpen}
                      aria-controls="bts-create-digits-panel"
                      className={`${linkButtonClass} ${linkTapClass}`}
                    >
                      {digitsOpen ? t("bts.create.digits_hide") : t("bts.create.digits_change")}
                    </button>
                  )}
                </div>
              </div>
              {takenCodes.length > 0 && (
                <p className="mt-2 flex items-start gap-1.5 text-sm text-amber-800">
                  <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {t("bts.create.codes_taken", { count: takenCodes.length, codes: takenCodes.join(", ") })}
                </p>
              )}
              {digitsOpen && (
                <div id="bts-create-digits-panel" className="mt-4 grid gap-4 border-t border-slate-200 pt-4 md:grid-cols-[12rem_minmax(0,1fr)]">
                  <p className="text-xs text-slate-500 md:col-span-2">{t("bts.create.codes_scheme")}</p>
                  <div>
                    <label htmlFor="bts-create-round_number" className={labelClass}>
                      {t("bts.fields.round_number")}
                    </label>
                    <input
                      id="bts-create-round_number"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={9}
                      value={round}
                      onChange={(e) => set({ round: e.target.value, roundEdited: true }, "round_number")}
                      aria-invalid={!!errors.round_number}
                      aria-describedby={describe("round_number", "bts-create-round-hint")}
                      className={`${fieldClass} h-10 max-sm:h-11 ${invalid("round_number")}`}
                    />
                    {mismatch ? (
                      <p id="bts-create-round-hint" className="mt-1.5 flex items-start gap-2 text-sm text-amber-800">
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <span className="min-w-0 flex-1 break-words">
                          {t("bts.create.round_mismatch", { folder: folder.name, folderRound, round: roundNumber })}{" "}
                          <button
                            type="button"
                            onClick={() => set({ round: "", roundEdited: false }, "round_number")}
                            className={`${linkButtonClass} ${linkTapClass} -my-1 py-0.5`}
                          >
                            {t("bts.create.use_folder_round", { round: folderRound })}
                          </button>
                        </span>
                      </p>
                    ) : (
                      <p id="bts-create-round-hint" className="mt-1.5 text-xs text-slate-500">
                        {folderRound ? t("bts.create.round_from_folder", { folder: folder.name, round: folderRound }) : t("bts.create.round_hint")}
                      </p>
                    )}
                    <FieldError id={errorId("round_number")}>{errors.round_number}</FieldError>
                  </div>
                  <VariantDigits
                    id="bts-create-digits"
                    value={digits}
                    onChange={(next) => set({ digits: next, digitsEdited: true }, "variant_numbers")}
                    round={roundNumber}
                    grade={Number(form.grade) || null}
                    taken={byDigit}
                    error={errors.variant_numbers}
                  />

                </div>
              )}
            </section>
          )}

          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_15rem]">
            <div>
              <label htmlFor="bts-create-name" className={labelClass}>
                {t("bts.fields.name")}
              </label>
              <input
                id="bts-create-name"
                type="text"
                value={name}
                maxLength={255}
                onChange={(e) => set({ name: e.target.value, nameEdited: true }, "name")}
                placeholder={t("bts.create.name_placeholder")}
                aria-invalid={!!errors.name}
                aria-describedby={describe("name")}
                className={`${fieldClass} h-10 max-sm:h-11 ${invalid("name")}`}
              />
              {form.nameEdited && suggestion && name !== suggestion && (
                <button type="button" onClick={() => set({ name: "", nameEdited: false }, "name")} className={`${linkButtonClass} ${linkTapClass} mt-1 text-left`}>
                  {t("bts.create.use_suggestion", { name: suggestion })}
                </button>
              )}
              <FieldError id={errorId("name")}>{errors.name}</FieldError>
            </div>

            <div>
              <label htmlFor="bts-create-duration_minutes" className={labelClass}>
                {t("bts.fields.duration")} <span className="font-normal text-slate-500">({t("bts.optional")})</span>
              </label>
              <input
                id="bts-create-duration_minutes"
                type="number"
                inputMode="numeric"
                min={1}
                max={1440}
                value={form.duration}
                onChange={(e) => set({ duration: e.target.value }, "duration_minutes")}
                placeholder="60"
                aria-invalid={!!errors.duration_minutes}
                aria-describedby={describe("duration_minutes")}
                className={`${fieldClass} h-10 max-sm:h-11 ${invalid("duration_minutes")}`}
              />
              <FieldError id={errorId("duration_minutes")}>{errors.duration_minutes}</FieldError>
            </div>
          </div>

          {showDescription || form.description ? (
            <div>
              <label htmlFor="bts-create-description" className={labelClass}>
                {t("bts.fields.description")} <span className="font-normal text-slate-500">({t("bts.optional")})</span>
              </label>
              <textarea
                id="bts-create-description"
                rows={3}
                value={form.description}
                onChange={(e) => set({ description: e.target.value }, "description")}
                aria-invalid={!!errors.description}
                aria-describedby={describe("description")}
                className={`${fieldClass} resize-y py-2 ${invalid("description")}`}
              />
              <FieldError id={errorId("description")}>{errors.description}</FieldError>
            </div>

          ) : (
            <button type="button" onClick={() => setShowDescription(true)} className={`${linkButtonClass} ${linkTapClass}`}>
              {t("bts.create.add_description")}
            </button>
          )}

          {formError && (
            <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {formError}
            </div>
          )}
        </div>

        <div className="-mx-4 -mb-4 mt-6 flex flex-col-reverse gap-2 rounded-b-2xl border-t border-slate-200 bg-slate-50/60 px-4 py-4 sm:-mx-6 sm:-mb-6 sm:flex-row sm:justify-end sm:px-6">
          <Link
            to={back}
            className={`inline-flex h-10 items-center justify-center rounded-lg bg-white px-5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 max-sm:h-11 ${focusRing}`}
          >
            {t("buttons.cancel")}
          </Link>
          <Button type="submit" color="primary" text={t("bts.create.submit")} loading={saving} className="max-sm:h-11" />
        </div>
      </form>
    </div>
  );
}
